import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles } from 'lucide-react';
import { Confetti } from '@/components/ui/confetti';
import { getAchievementByType } from './achievementDefinitions';
import { Button } from '@/components/ui/button';

interface AchievementUnlockedModalProps {
  achievementType: string | null;
  onClose: () => void;
}

export const AchievementUnlockedModal: React.FC<AchievementUnlockedModalProps> = ({
  achievementType,
  onClose
}) => {
  const achievement = achievementType ? getAchievementByType(achievementType) : null;

  useEffect(() => {
    if (achievementType) {
      // Auto-close after 5 seconds
      const timer = setTimeout(onClose, 5000);
      return () => clearTimeout(timer);
    }
  }, [achievementType, onClose]);

  if (!achievement) return null;

  return (
    <AnimatePresence>
      {achievementType && (
        <>
          <Confetti isActive={true} duration={4000} />
          
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[10000] flex items-center justify-center p-4"
            onClick={onClose}
          >
            <motion.div
              initial={{ scale: 0.5, opacity: 0, y: 50 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.5, opacity: 0, y: 50 }}
              transition={{ type: 'spring', damping: 15 }}
              className="bg-gradient-to-b from-amber-900/50 to-black/90 border border-amber-500/50 rounded-3xl p-8 max-w-sm w-full text-center relative overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Background glow */}
              <div className="absolute inset-0 bg-gradient-to-r from-amber-500/10 via-yellow-500/10 to-orange-500/10 animate-pulse" />
              
              {/* Close button */}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 text-white/50 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="relative z-10">
                {/* Header */}
                <motion.div
                  initial={{ y: -20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className="flex items-center justify-center gap-2 text-amber-400 mb-4"
                >
                  <Sparkles className="w-5 h-5" />
                  <span className="text-sm font-semibold tracking-wider uppercase">
                    ¡Nuevo Logro!
                  </span>
                  <Sparkles className="w-5 h-5" />
                </motion.div>

                {/* Badge */}
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ delay: 0.3, type: 'spring', damping: 10 }}
                  className="w-24 h-24 mx-auto mb-4 rounded-full bg-gradient-to-br from-amber-500/30 to-orange-600/30 border-2 border-amber-500/50 flex items-center justify-center"
                >
                  <span className="text-5xl">{achievement.badge}</span>
                </motion.div>

                {/* Title */}
                <motion.h2
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  className="text-2xl font-bold text-white mb-2"
                >
                  {achievement.name}
                </motion.h2>

                {/* Description */}
                <motion.p
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className="text-white/70 mb-4"
                >
                  {achievement.description}
                </motion.p>

                {/* Points */}
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.6, type: 'spring' }}
                  className="inline-flex items-center gap-2 bg-amber-500/20 border border-amber-500/30 rounded-full px-4 py-2 mb-6"
                >
                  <span className="text-amber-400 font-bold">+{achievement.points}</span>
                  <span className="text-white/70 text-sm">puntos en ranking</span>
                </motion.div>

                {/* Button */}
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.7 }}
                >
                  <Button
                    onClick={onClose}
                    className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-black font-bold px-8"
                  >
                    ¡Genial!
                  </Button>
                </motion.div>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default AchievementUnlockedModal;
